import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, GripVertical, Plus } from 'lucide-react';
import {
  DndContext,
  DragOverEvent,
  DragStartEvent,
  DragEndEvent,
  closestCorners,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RichTextField, type MentionableEntity } from './RichTextField';
import { InlineEditableText } from './InlineEditableText';
import { useSortableSensors } from '../hooks/use-sortable-sensors';
import type { Chapter } from './Chapters';
import type { Scene } from './Scenes';
import { stripHtml } from '../utils/note-utils';
import {
  UNLINKED_SCENES_CONTAINER_ID,
  findChapterContainerId,
  isUnlinkedScenesContainer,
  recordToScenesByChapter,
  scenesByChapterToRecordWithUnlinked,
} from '../utils/chapter-scene-order';
import {
  flattenAvailableScenes,
  type AvailableScenesTree,
} from '../utils/available-scenes-tree';
import { cn } from '@/lib/utils';
import { RICH_TEXT_HTML_CLASS } from '../utils/rich-text-html';
import { Button } from './ui/button';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { useToast } from '../contexts/ToastContext';

export const CHAPTER_LIST_SECTION_CLASSNAME =
  'w-full scroll-mt-4 border-b border-border/60 py-8 first:pt-0 last:border-b-0';

export interface ChapterSceneDragEndResult {
  scenesByChapter: Map<string, Scene[]>;
  sceneId: string;
  sceneDocumentId?: string;
  fromChapterId: string;
  toChapterId: string;
}

interface ChaptersListViewProps {
  chapters: Chapter[];
  scenesByChapter: Map<string, Scene[]>;
  unlinkedScenes?: Scene[];
  availableScenesTree?: AvailableScenesTree;
  mentionableEntities: MentionableEntity[];
  listContentClassName?: string;
  currentDocumentId?: string;
  onEditChapter: (chapter: Chapter) => void;
  onTitleChange: (chapter: Chapter, title: string) => void | boolean | Promise<void | boolean>;
  onSummaryChange: (chapter: Chapter, summary: string) => void;
  onAddScene: (chapter: Chapter) => void;
  onEditScene?: (scene: Scene, chapter?: Chapter) => void;
  onSceneDragEnd?: (result: ChapterSceneDragEndResult) => void | Promise<void>;
  onAddChapter?: () => void;
  t: (key: string) => string;
}

function formatWordCount(count: number | undefined): string | null {
  if (count == null || count <= 0) return null;
  return `${count.toLocaleString()}w`;
}

function chapterWordLabel(chapter: Chapter): string | null {
  return formatWordCount(chapter.wordCount) ?? formatWordCount(chapter.wordsGoal);
}

function normalizeChapterSummary(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return '';
  if (trimmed === '<p></p>' || trimmed === '<p><br></p>' || trimmed === '<p><br/></p>') return '';
  return trimmed;
}

function sceneCardSummary(scene: Scene): string | null {
  const fromDescription = scene.description ? stripHtml(scene.description).trim() : '';
  if (fromDescription) return fromDescription;
  return null;
}

const SortableChapterSceneCard: React.FC<{
  scene: Scene;
  onClick?: () => void;
  t: (key: string) => string;
  className?: string;
  currentDocumentId?: string;
}> = ({ scene, onClick, t, className, currentDocumentId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: scene.id });
  const suppressClickAfterDragRef = useRef(false);

  useEffect(() => {
    if (isDragging) {
      suppressClickAfterDragRef.current = true;
    }
  }, [isDragging]);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    touchAction: 'none',
  };

  const summary = sceneCardSummary(scene);
  const sourceDocumentTitle =
    scene.documentId &&
    currentDocumentId &&
    scene.documentId !== currentDocumentId
      ? scene.documentTitle?.trim() || null
      : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative flex h-[148px] w-[200px] shrink-0 cursor-grab flex-col gap-2 rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-shadow active:cursor-grabbing',
        onClick && 'hover:shadow-md',
        className,
      )}
      {...attributes}
      {...listeners}
      aria-label={t('toc_drag_section_tooltip')}
      data-testid="chapter-scene-card"
    >
      <div
        className="pointer-events-none absolute right-2 top-2 z-10 text-muted-foreground"
        aria-hidden="true"
        data-testid="chapter-scene-card-drag-handle"
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <button
        type="button"
        onClick={() => {
          if (suppressClickAfterDragRef.current) {
            suppressClickAfterDragRef.current = false;
            return;
          }
          onClick?.();
        }}
        disabled={!onClick}
        className={cn(
          'flex min-h-0 flex-1 cursor-inherit flex-col gap-2 text-left',
          !onClick && 'cursor-grab',
        )}
      >
        {sourceDocumentTitle ? (
          <span className="line-clamp-1 pr-6 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            {sourceDocumentTitle}
          </span>
        ) : null}
        <h4 className="line-clamp-2 pr-6 text-sm font-semibold leading-snug text-foreground">
          {scene.title?.trim() || t('scene_card_summary_placeholder')}
        </h4>
        {summary ? (
          <p className="line-clamp-4 text-sm leading-snug text-muted-foreground">
            {summary}
          </p>
        ) : null}
      </button>
    </div>
  );
};

const ChapterScenesDroppable: React.FC<{
  chapterId: string;
  scenes: Scene[];
  onAddScene: () => void;
  onEditScene?: (scene: Scene) => void;
  t: (key: string) => string;
  currentDocumentId?: string;
}> = ({ chapterId, scenes, onAddScene, onEditScene, t, currentDocumentId }) => {
  const { setNodeRef, isOver } = useDroppable({ id: chapterId });

  return (
    <SortableContext items={scenes.map((scene) => scene.id)} strategy={horizontalListSortingStrategy}>
      <div
        ref={setNodeRef}
        className={cn(
          'flex w-full items-stretch gap-4 overflow-x-auto rounded-xl pb-1 transition-colors',
          isOver && 'bg-muted/40',
        )}
      >
        {scenes.map((scene) => (
          <SortableChapterSceneCard
            key={scene.id}
            scene={scene}
            onClick={onEditScene ? () => onEditScene(scene) : undefined}
            t={t}
            currentDocumentId={currentDocumentId}
          />
        ))}
        <button
          type="button"
          onClick={onAddScene}
          className="flex h-[148px] w-[200px] shrink-0 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-transparent p-4 text-sm font-medium text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
        >
          <Plus className="h-5 w-5" />
          {t('add_or_link_scene')}
        </button>
      </div>
    </SortableContext>
  );
};

const ChapterListSection: React.FC<{
  chapter: Chapter;
  scenes: Scene[];
  mentionableEntities: MentionableEntity[];
  onEditChapter: (chapter: Chapter) => void;
  onTitleChange: (chapter: Chapter, title: string) => void | boolean | Promise<void | boolean>;
  onSummaryChange: (chapter: Chapter, summary: string) => void;
  onAddScene: (chapter: Chapter) => void;
  onEditScene?: (scene: Scene, chapter?: Chapter) => void;
  t: (key: string) => string;
  currentDocumentId?: string;
}> = ({
  chapter,
  scenes,
  mentionableEntities,
  onEditChapter,
  onTitleChange,
  onSummaryChange,
  onAddScene,
  onEditScene,
  t,
  currentDocumentId,
}) => {
  const { showFieldSavedToast } = useToast();
  const order = chapter.order ?? chapter.number ?? 0;
  const wordLabel = chapterWordLabel(chapter);
  const [title, setTitle] = useState(chapter.title ?? '');
  const lastPersistedTitleRef = useRef(chapter.title ?? '');
  const onTitleChangeRef = useRef(onTitleChange);
  onTitleChangeRef.current = onTitleChange;
  const [summary, setSummary] = useState(chapter.summary ?? '');
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const latestSummaryRef = useRef(summary);
  const lastPersistedRef = useRef(chapter.summary ?? '');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const summaryEditorContainerRef = useRef<HTMLDivElement>(null);
  const isCancellingSummaryRef = useRef(false);
  const onSummaryChangeRef = useRef(onSummaryChange);
  onSummaryChangeRef.current = onSummaryChange;
  const chapterRef = useRef(chapter);
  chapterRef.current = chapter;

  const persistTitle = useCallback(async (nextTitle: string): Promise<boolean> => {
    const trimmed = nextTitle.trim();
    if (!trimmed) {
      setTitle(lastPersistedTitleRef.current);
      return false;
    }
    if (trimmed === lastPersistedTitleRef.current.trim()) return false;
    lastPersistedTitleRef.current = trimmed;
    setTitle(trimmed);
    const result = await onTitleChangeRef.current(chapterRef.current, trimmed);
    return result !== false;
  }, []);

  useEffect(() => {
    const incoming = chapter.title ?? '';
    if (incoming === lastPersistedTitleRef.current) return;
    setTitle(incoming);
    lastPersistedTitleRef.current = incoming;
  }, [chapter.title]);

  const persistSummary = useCallback((html: string): void => {
    const normalized = normalizeChapterSummary(html);
    if (normalized === normalizeChapterSummary(lastPersistedRef.current)) return;
    lastPersistedRef.current = normalized;
    onSummaryChangeRef.current(chapterRef.current, normalized);
    showFieldSavedToast(t('summary'));
  }, [showFieldSavedToast, t]);

  useEffect(() => {
    const incoming = chapter.summary ?? '';
    if (normalizeChapterSummary(incoming) === normalizeChapterSummary(lastPersistedRef.current)) {
      return;
    }
    setSummary(incoming);
    latestSummaryRef.current = incoming;
    lastPersistedRef.current = incoming;
  }, [chapter.summary]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        persistSummary(latestSummaryRef.current);
      }
    };
  }, [persistSummary]);

  const handleSummaryChange = (html: string): void => {
    setSummary(html);
    latestSummaryRef.current = html;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      persistSummary(html);
      saveTimeoutRef.current = null;
    }, 500);
  };

  const stopEditingSummary = (): void => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    persistSummary(latestSummaryRef.current);
    setIsEditingSummary(false);
  };

  const cancelEditingSummary = (): void => {
    isCancellingSummaryRef.current = true;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    const restored = lastPersistedRef.current;
    setSummary(restored);
    latestSummaryRef.current = restored;
    setIsEditingSummary(false);
  };

  const hasSummaryContent = Boolean(normalizeChapterSummary(summary));
  const chapterId = chapter.id ?? '';

  return (
    <section
      data-chapter-id={chapter.id || undefined}
      className={CHAPTER_LIST_SECTION_CLASSNAME}
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onEditChapter(chapter)}
            className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground"
          >
            {t('chapter_label').replace('{n}', String(order || '-'))}
          </button>
          <InlineEditableText
            value={title}
            onCommit={persistTitle}
            savedFieldLabel={t('title')}
            placeholder={t('chapter_title_placeholder')}
            as="h3"
            className="mt-1 max-w-3xl truncate text-2xl font-semibold tracking-tight text-foreground"
            inputClassName="mt-1 max-w-3xl text-2xl font-semibold tracking-tight"
            displayClassName="block"
          />
          <div className="mt-3 max-w-3xl">
            {isEditingSummary ? (
              <div
                ref={summaryEditorContainerRef}
                onBlur={(event) => {
                  if (isCancellingSummaryRef.current) {
                    isCancellingSummaryRef.current = false;
                    return;
                  }
                  const nextFocus = event.relatedTarget as Node | null;
                  if (summaryEditorContainerRef.current?.contains(nextFocus)) return;
                  stopEditingSummary();
                }}
              >
                <RichTextField
                  value={summary}
                  onChange={handleSummaryChange}
                  placeholder={t('chapter_summary_edit_placeholder')}
                  minHeight="72px"
                  mentionableEntities={mentionableEntities}
                  autoFocus
                  onSubmit={stopEditingSummary}
                  onCancel={cancelEditingSummary}
                  submitHint={t('chapter_summary_submit_hint').replace(
                    '{mod}',
                    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
                      ? '⌘'
                      : 'Ctrl',
                  )}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditingSummary(true)}
                className={cn(
                  'w-full rounded-md border border-transparent px-3 py-2 text-left transition-[border-color,background-color] duration-150',
                  'cursor-text hover:border-border hover:bg-muted/40',
                  'focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30',
                )}
              >
                {hasSummaryContent ? (
                  <div
                    className={`${RICH_TEXT_HTML_CLASS} text-sm text-foreground/80 [&_p]:my-0 [&_p:not(:last-child)]:mb-2`}
                    dangerouslySetInnerHTML={{ __html: summary }}
                  />
                ) : (
                  <span className="text-sm italic text-muted-foreground/20">
                    {t('chapter_summary_edit_placeholder')}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {wordLabel ? (
            <span className="pr-1 text-xs text-muted-foreground">{wordLabel}</span>
          ) : null}
        </div>
      </div>

      {chapterId ? (
        <ChapterScenesDroppable
          chapterId={chapterId}
          scenes={scenes}
          onAddScene={() => onAddScene(chapter)}
          onEditScene={onEditScene ? (scene) => onEditScene(scene, chapter) : undefined}
          t={t}
          currentDocumentId={currentDocumentId}
        />
      ) : null}
    </section>
  );
};

const AvailableScenesSection: React.FC<{
  title: string;
  count: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  emptyLabel?: string;
  className?: string;
}> = ({ title, count, open, onOpenChange, children, emptyLabel, className }) => {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className={cn('min-w-0', className)}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-1 rounded-md px-1 py-1.5 text-left transition-colors hover:bg-muted/60"
        >
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
              open ? 'rotate-0' : '-rotate-90',
            )}
          />
          <span className="min-w-0 flex-1 truncate text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            {title}
          </span>
          <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground/80">{count}</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-2 pb-2 pl-1">
          {count > 0 ? children : (
            <p className="px-1 py-2 text-center text-[11px] text-muted-foreground">
              {emptyLabel}
            </p>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const AvailableScenesPanel: React.FC<{
  tree: AvailableScenesTree;
  poolScenes: Scene[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEditScene?: (scene: Scene) => void;
  t: (key: string) => string;
  currentDocumentId?: string;
}> = ({ tree, poolScenes, open, onOpenChange, onEditScene, t, currentDocumentId }) => {
  const { setNodeRef, isOver } = useDroppable({ id: UNLINKED_SCENES_CONTAINER_ID });
  const poolIds = useMemo(() => new Set(poolScenes.map((scene) => scene.id)), [poolScenes]);
  const otherDocumentSceneIds = useMemo(() => {
    const ids = new Set<string>();
    for (const document of tree.documents) {
      for (const scene of document.unlinkedScenes) ids.add(scene.id);
      for (const chapter of document.chapters) {
        for (const scene of chapter.scenes) ids.add(scene.id);
      }
    }
    return ids;
  }, [tree.documents]);
  const currentUnlinkedScenes = useMemo(
    () => poolScenes.filter((scene) => !otherDocumentSceneIds.has(scene.id)),
    [poolScenes, otherDocumentSceneIds],
  );
  const visibleDocuments = useMemo(
    () =>
      tree.documents
        .map((document) => ({
          ...document,
          unlinkedScenes: document.unlinkedScenes.filter((scene) => poolIds.has(scene.id)),
          chapters: document.chapters
            .map((chapter) => ({
              ...chapter,
              scenes: chapter.scenes.filter((scene) => poolIds.has(scene.id)),
            }))
            .filter((chapter) => chapter.scenes.length > 0),
        }))
        .filter(
          (document) => document.unlinkedScenes.length > 0 || document.chapters.length > 0,
        ),
    [tree.documents, poolIds],
  );
  const availableCount = poolScenes.length;
  const toggleTooltip = open
    ? t('collapse_unlinked_scenes_tooltip')
    : t('expand_unlinked_scenes_tooltip');

  const [currentUnlinkedOpen, setCurrentUnlinkedOpen] = useState(true);
  const [openDocumentIds, setOpenDocumentIds] = useState<Set<string>>(() => new Set());
  const [openDocumentUnlinkedIds, setOpenDocumentUnlinkedIds] = useState<Set<string>>(() => new Set());
  const [openChapterKeys, setOpenChapterKeys] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!open) return;
    setCurrentUnlinkedOpen(true);
  }, [open]);

  const setDocumentOpen = (documentId: string, nextOpen: boolean): void => {
    setOpenDocumentIds((prev) => {
      const next = new Set(prev);
      if (nextOpen) next.add(documentId);
      else next.delete(documentId);
      return next;
    });
    if (nextOpen) {
      setOpenDocumentUnlinkedIds((prev) => new Set(prev).add(documentId));
    }
  };

  const setDocumentUnlinkedOpen = (documentId: string, nextOpen: boolean): void => {
    setOpenDocumentUnlinkedIds((prev) => {
      const next = new Set(prev);
      if (nextOpen) next.add(documentId);
      else next.delete(documentId);
      return next;
    });
  };

  const setChapterOpen = (chapterKey: string, nextOpen: boolean): void => {
    setOpenChapterKeys((prev) => {
      const next = new Set(prev);
      if (nextOpen) next.add(chapterKey);
      else next.delete(chapterKey);
      return next;
    });
  };

  const renderSceneCards = (scenes: Scene[]): React.ReactNode =>
    scenes.map((scene) => (
      <SortableChapterSceneCard
        key={`${scene.documentId ?? currentDocumentId ?? 'scene'}:${scene.id}`}
        scene={scene}
        onClick={onEditScene ? () => onEditScene(scene) : undefined}
        t={t}
        className="h-auto min-h-[100px] w-full"
        currentDocumentId={currentDocumentId}
      />
    ));

  return (
    <aside
      className={cn(
        'flex h-full min-h-0 shrink-0 flex-col overflow-hidden border-l border-border bg-muted/20 transition-[width] duration-200',
        open ? 'w-[232px]' : 'w-10',
      )}
    >
      <div
        className={cn(
          'flex shrink-0 items-center gap-1 border-b border-border px-1 py-2',
          open ? 'justify-between px-2' : 'flex-col justify-start gap-2',
        )}
      >
        {open ? (
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              {t('available_scenes')}
            </h3>
            <p className="truncate text-[11px] text-muted-foreground/80">
              {availableCount === 0
                ? t('available_scenes_empty')
                : t('available_scenes_count').replace('{count}', String(availableCount))}
            </p>
          </div>
        ) : null}
        <TooltipProvider delayDuration={300}>
          <ButtonTooltip tooltip={toggleTooltip} side="left">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => onOpenChange(!open)}
              aria-label={toggleTooltip}
              aria-expanded={open}
            >
              {open ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </ButtonTooltip>
        </TooltipProvider>
        {!open && availableCount > 0 ? (
          <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {availableCount}
          </span>
        ) : null}
      </div>
      <SortableContext
        items={poolScenes.map((scene) => scene.id)}
        strategy={verticalListSortingStrategy}
      >
        <div
          ref={setNodeRef}
          className={cn(
            'min-h-0 flex-1 overflow-y-auto transition-colors',
            open ? 'p-2' : 'p-1',
            isOver && 'bg-muted/50',
          )}
        >
          {open ? (
            <div className="flex flex-col gap-1">
              <AvailableScenesSection
                title={t('unlinked_scenes')}
                count={currentUnlinkedScenes.length}
                open={currentUnlinkedOpen}
                onOpenChange={setCurrentUnlinkedOpen}
                emptyLabel={t('unlinked_scenes_drop_hint')}
              >
                {renderSceneCards(currentUnlinkedScenes)}
              </AvailableScenesSection>

              {visibleDocuments.map((document) => {
                const documentOpen = openDocumentIds.has(document.documentId);
                const documentSceneCount =
                  document.unlinkedScenes.length +
                  document.chapters.reduce((sum, chapter) => sum + chapter.scenes.length, 0);
                return (
                  <AvailableScenesSection
                    key={document.documentId}
                    title={document.documentTitle}
                    count={documentSceneCount}
                    open={documentOpen}
                    onOpenChange={(nextOpen) => setDocumentOpen(document.documentId, nextOpen)}
                    className="mt-1 border-t border-border/60 pt-1"
                  >
                    <AvailableScenesSection
                      title={t('unlinked_scenes')}
                      count={document.unlinkedScenes.length}
                      open={openDocumentUnlinkedIds.has(document.documentId)}
                      onOpenChange={(nextOpen) =>
                        setDocumentUnlinkedOpen(document.documentId, nextOpen)
                      }
                      emptyLabel={t('unlinked_scenes_empty')}
                    >
                      {renderSceneCards(document.unlinkedScenes)}
                    </AvailableScenesSection>
                    {document.chapters.map((chapter) => {
                      const chapterKey = `${document.documentId}:${chapter.id}`;
                      return (
                        <AvailableScenesSection
                          key={chapterKey}
                          title={chapter.title}
                          count={chapter.scenes.length}
                          open={openChapterKeys.has(chapterKey)}
                          onOpenChange={(nextOpen) => setChapterOpen(chapterKey, nextOpen)}
                          emptyLabel={t('unlinked_scenes_empty')}
                        >
                          {renderSceneCards(chapter.scenes)}
                        </AvailableScenesSection>
                      );
                    })}
                  </AvailableScenesSection>
                );
              })}
            </div>
          ) : (
            <div className="flex h-full min-h-[120px] items-start justify-center pt-2">
              <span
                className="select-none text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                style={{ writingMode: 'vertical-rl' }}
              >
                {t('available_scenes')}
              </span>
            </div>
          )}
        </div>
      </SortableContext>
    </aside>
  );
};

export const ChaptersListView: React.FC<ChaptersListViewProps> = ({
  chapters,
  scenesByChapter,
  unlinkedScenes = [],
  availableScenesTree,
  mentionableEntities,
  listContentClassName,
  currentDocumentId,
  onEditChapter,
  onTitleChange,
  onSummaryChange,
  onAddScene,
  onEditScene,
  onSceneDragEnd,
  onAddChapter,
  t,
}) => {
  const sensors = useSortableSensors();
  const sortedChapters = useMemo(
    () => [...chapters].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [chapters],
  );
  const resolvedAvailableScenesTree = useMemo<AvailableScenesTree>(
    () =>
      availableScenesTree ?? {
        currentUnlinkedScenes: unlinkedScenes,
        documents: [],
      },
    [availableScenesTree, unlinkedScenes],
  );
  const availableScenes = useMemo(
    () => flattenAvailableScenes(resolvedAvailableScenesTree),
    [resolvedAvailableScenesTree],
  );

  const [items, setItems] = useState<Record<string, Scene[]>>(() =>
    scenesByChapterToRecordWithUnlinked(sortedChapters, scenesByChapter, availableScenes),
  );
  const itemsRef = useRef(items);
  const dragOriginRef = useRef<{
    sceneId: string;
    fromChapterId: string;
    sceneDocumentId?: string;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [unlinkedPanelOpen, setUnlinkedPanelOpen] = useState(availableScenes.length > 0);
  const previousAvailableCountRef = useRef(availableScenes.length);

  const replaceItems = useCallback((next: Record<string, Scene[]>): void => {
    itemsRef.current = next;
    setItems(next);
  }, []);

  const updateItems = useCallback(
    (updater: (prev: Record<string, Scene[]>) => Record<string, Scene[]>): void => {
      const next = updater(itemsRef.current);
      if (next === itemsRef.current) return;
      itemsRef.current = next;
      setItems(next);
    },
    [],
  );

  useEffect(() => {
    if (isDragging) return;
    replaceItems(scenesByChapterToRecordWithUnlinked(sortedChapters, scenesByChapter, availableScenes));
  }, [scenesByChapter, sortedChapters, availableScenes, isDragging, replaceItems]);

  useEffect(() => {
    const previousCount = previousAvailableCountRef.current;
    previousAvailableCountRef.current = availableScenes.length;
    if (previousCount === 0 && availableScenes.length > 0) {
      setUnlinkedPanelOpen(true);
    }
  }, [availableScenes.length]);

  const moveSceneToContainer = (
    prev: Record<string, Scene[]>,
    activeId: string,
    overId: string,
    activeContainer: string,
    overContainer: string,
  ): Record<string, Scene[]> => {
    const activeScenes = prev[activeContainer] ?? [];
    const overScenes = prev[overContainer] ?? [];
    const activeIndex = activeScenes.findIndex((scene) => scene.id === activeId);
    if (activeIndex === -1) return prev;

    const activeScene = activeScenes[activeIndex];
    const overIndex =
      overId in prev
        ? overScenes.length
        : overScenes.findIndex((scene) => scene.id === overId);
    const insertIndex = overIndex < 0 ? overScenes.length : overIndex;

    return {
      ...prev,
      [activeContainer]: activeScenes.filter((scene) => scene.id !== activeId),
      [overContainer]: [
        ...overScenes.slice(0, insertIndex),
        activeScene,
        ...overScenes.slice(insertIndex),
      ],
    };
  };

  const handleDragStart = (event: DragStartEvent): void => {
    const sceneId = String(event.active.id);
    const fromChapterId = findChapterContainerId(itemsRef.current, sceneId);
    if (!fromChapterId) return;
    const originScene = (itemsRef.current[fromChapterId] ?? []).find((scene) => scene.id === sceneId);
    dragOriginRef.current = {
      sceneId,
      fromChapterId,
      sceneDocumentId: originScene?.documentId,
    };
    setIsDragging(true);
    if (isUnlinkedScenesContainer(fromChapterId) && !unlinkedPanelOpen) {
      setUnlinkedPanelOpen(true);
    }
  };

  const handleDragOver = (event: DragOverEvent): void => {
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    const activeContainer = findChapterContainerId(itemsRef.current, activeId);
    const overContainer = findChapterContainerId(itemsRef.current, overId);
    if (!activeContainer || !overContainer || activeContainer === overContainer) {
      return;
    }

    if (isUnlinkedScenesContainer(overContainer) && !unlinkedPanelOpen) {
      setUnlinkedPanelOpen(true);
    }

    updateItems((prev) =>
      moveSceneToContainer(prev, activeId, overId, activeContainer, overContainer),
    );
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    const origin = dragOriginRef.current;
    dragOriginRef.current = null;

    const finishDragging = (): void => {
      setIsDragging(false);
    };

    if (!over || !origin) {
      replaceItems(scenesByChapterToRecordWithUnlinked(sortedChapters, scenesByChapter, availableScenes));
      finishDragging();
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);
    const prev = itemsRef.current;
    const activeContainer = findChapterContainerId(prev, activeId);
    const overContainer = findChapterContainerId(prev, overId);

    if (!activeContainer || !overContainer) {
      replaceItems(scenesByChapterToRecordWithUnlinked(sortedChapters, scenesByChapter, availableScenes));
      finishDragging();
      return;
    }

    let next = prev;
    if (activeContainer === overContainer) {
      const containerScenes = prev[activeContainer];
      const oldIndex = containerScenes.findIndex((scene) => scene.id === activeId);
      const newIndex =
        overId in prev
          ? containerScenes.length - 1
          : containerScenes.findIndex((scene) => scene.id === overId);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        next = {
          ...prev,
          [activeContainer]: arrayMove(containerScenes, oldIndex, newIndex),
        };
      }
    } else {
      next = moveSceneToContainer(prev, activeId, overId, activeContainer, overContainer);
    }

    replaceItems(next);

    const toChapterId = findChapterContainerId(next, activeId) ?? overContainer;
    const membershipChanged = origin.fromChapterId !== toChapterId;
    const beforeIds = (
      isUnlinkedScenesContainer(origin.fromChapterId)
        ? availableScenes
        : (scenesByChapter.get(origin.fromChapterId) ?? [])
    ).map((scene) => scene.id);
    const afterToIds = (next[toChapterId] ?? []).map((scene) => scene.id);
    const orderChanged = membershipChanged
      ? true
      : beforeIds.length !== afterToIds.length ||
        beforeIds.some((id, index) => id !== afterToIds[index]);

    if ((!membershipChanged && !orderChanged) || !onSceneDragEnd) {
      finishDragging();
      return;
    }

    void Promise.resolve(
      onSceneDragEnd({
        scenesByChapter: recordToScenesByChapter(next),
        sceneId: origin.sceneId,
        sceneDocumentId: origin.sceneDocumentId,
        fromChapterId: origin.fromChapterId,
        toChapterId,
      }),
    ).finally(finishDragging);
  };

  const handleDragCancel = (): void => {
    dragOriginRef.current = null;
    setIsDragging(false);
    replaceItems(scenesByChapterToRecordWithUnlinked(sortedChapters, scenesByChapter, availableScenes));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex min-h-0 w-full flex-1 items-stretch overflow-hidden">
        <div className={cn('min-h-0 min-w-0 flex-1 overflow-y-auto', listContentClassName)}>
          {sortedChapters.map((chapter) => {
            const chapterId = chapter.id ?? '';
            const scenes = chapterId ? (items[chapterId] ?? []) : [];
            return (
              <ChapterListSection
                key={chapter.id ?? chapter.title}
                chapter={chapter}
                scenes={scenes}
                mentionableEntities={mentionableEntities}
                onEditChapter={onEditChapter}
                onTitleChange={onTitleChange}
                onSummaryChange={onSummaryChange}
                onAddScene={onAddScene}
                onEditScene={onEditScene}
                t={t}
                currentDocumentId={currentDocumentId}
              />
            );
          })}
          {onAddChapter ? (
            <button
              type="button"
              onClick={onAddChapter}
              className="mt-2 mb-2 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border py-3 text-sm font-medium text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
            >
              <Plus className="h-4 w-4" />
              {t('add_chapter')}
            </button>
          ) : null}
        </div>
        <AvailableScenesPanel
          tree={resolvedAvailableScenesTree}
          poolScenes={items[UNLINKED_SCENES_CONTAINER_ID] ?? []}
          open={unlinkedPanelOpen}
          onOpenChange={setUnlinkedPanelOpen}
          onEditScene={onEditScene ? (scene) => onEditScene(scene) : undefined}
          t={t}
          currentDocumentId={currentDocumentId}
        />
      </div>
    </DndContext>
  );
};
